import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Trophy, Loader2, RefreshCw, ChevronRight, CircleAlert, CheckCircle2,
  AlertTriangle, XCircle, FileText, BookOpenCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { thesisApi } from '@/api/thesisApi';

/**
 * ProjectReadinessOverviewDialog — Sprint 6+ aggregated dashboard.
 *
 * Reads each chapter's persisted `last_readiness` snapshot from the new
 * `GET /api/thesis/projects/{id}/readiness-overview` endpoint and renders:
 *   • Project-level word-weighted score ring
 *   • 4-bucket breakdown (ready / almost / needs work / major / not audited)
 *   • Per-chapter list with score chip + words + audited-at age + Open button
 *   • Top high-priority aggregated action items across all chapters
 *
 * Does NOT trigger LLM calls. Author runs the per-chapter audit first
 * (Readiness button in editor toolbar); this view aggregates the snapshots.
 */
function scoreColor(s) {
  if (s >= 80) return 'text-emerald-600 dark:text-emerald-400';
  if (s >= 60) return 'text-amber-600 dark:text-amber-400';
  if (s >= 40) return 'text-orange-600 dark:text-orange-400';
  return 'text-red-600 dark:text-red-400';
}
function ringStroke(s) {
  if (s >= 80) return '#10b981';
  if (s >= 60) return '#f59e0b';
  if (s >= 40) return '#f97316';
  return '#ef4444';
}

function ScoreRing({ score }) {
  const radius = 56;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (Math.max(0, Math.min(100, score)) / 100) * circ;
  return (
    <svg width="140" height="140" viewBox="0 0 140 140" data-testid="project-readiness-ring">
      <circle cx="70" cy="70" r={radius} fill="none" stroke="#e2e8f0" strokeWidth="10" className="dark:stroke-slate-700" />
      <circle cx="70" cy="70" r={radius} fill="none" stroke={ringStroke(score)} strokeWidth="10"
        strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
        transform="rotate(-90 70 70)" style={{ transition: 'stroke-dashoffset 0.8s ease' }} />
      <text x="70" y="74" textAnchor="middle" fontSize="32" fontWeight="700"
        className="fill-slate-800 dark:fill-slate-100">{score}</text>
      <text x="70" y="96" textAnchor="middle" fontSize="9"
        className="fill-slate-500 dark:fill-slate-400 uppercase tracking-wider">project</text>
    </svg>
  );
}

function relTime(iso) {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return '—';
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function ProjectReadinessOverviewDialog({
  open, onOpenChange, userId, projectId, onOpenChapter,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const res = await thesisApi.getProjectReadinessOverview(userId, projectId);
      setData(res);
    } catch (e) {
      toast.error(`Could not load project readiness: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, [userId, projectId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const score = data?.overall_score ?? 0;
  const buckets = data?.buckets || {};
  const chapters = data?.chapters || [];
  const actions = data?.aggregated_actions || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="project-readiness-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Trophy className="h-5 w-5 text-amber-500" />
            Project Readiness Overview
          </DialogTitle>
          <DialogDescription>
            Aggregated readiness scores across all chapters of this project.
            Audit each chapter individually (per-chapter Readiness button) to populate this view.
          </DialogDescription>
        </DialogHeader>

        {loading && !data && (
          <div className="py-16 text-center">
            <Loader2 className="h-10 w-10 animate-spin mx-auto text-amber-500" />
            <p className="text-sm text-slate-600 mt-3">Loading project snapshot…</p>
          </div>
        )}

        {data && (
          <div className="space-y-5">
            {/* Header ring + buckets */}
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-gradient-to-br from-amber-50/60 to-violet-50/40 dark:from-amber-950/20 dark:to-violet-950/20 p-5 flex flex-col md:flex-row items-center gap-6">
              <ScoreRing score={score} />
              <div className="flex-1 min-w-0 w-full">
                <div className={`text-xl font-bold mb-1 ${scoreColor(score)}`}>
                  {score >= 80 ? 'Project ready for submission'
                    : score >= 60 ? 'Almost there'
                    : score >= 40 ? 'Major sections still need work'
                    : data.audited_count === 0 ? 'No chapters audited yet'
                    : 'Major revisions required'}
                </div>
                <p className="text-xs text-slate-500 mb-3" data-testid="project-readiness-meta">
                  {data.audited_count}/{data.total_count} chapters audited
                  {data.checked_at && <> · most recent audit {relTime(data.checked_at)}</>}
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                  <BucketPill label="Ready" count={buckets.ready || 0} icon={CheckCircle2} color="emerald" testid="bucket-ready" />
                  <BucketPill label="Almost" count={buckets.almost || 0} icon={CheckCircle2} color="amber" testid="bucket-almost" />
                  <BucketPill label="Needs work" count={buckets.needs_work || 0} icon={AlertTriangle} color="orange" testid="bucket-needs-work" />
                  <BucketPill label="Major" count={buckets.major || 0} icon={XCircle} color="red" testid="bucket-major" />
                  <BucketPill label="Not audited" count={buckets.not_audited || 0} icon={CircleAlert} color="slate" testid="bucket-not-audited" />
                </div>
              </div>
            </div>

            {/* Aggregated high-priority action items */}
            {actions.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold mb-2 text-slate-700 dark:text-slate-200 uppercase tracking-wider flex items-center gap-2">
                  <FileText className="h-4 w-4" /> Top Priority Actions ({actions.length})
                </h4>
                <ol className="space-y-2" data-testid="aggregated-actions-list">
                  {actions.map((a, i) => (
                    <li key={i} className="rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 flex items-start gap-3"
                      data-testid={`aggregated-action-${i}`}>
                      <CircleAlert className="h-4 w-4 mt-0.5 shrink-0 text-red-500" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-0.5">
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full uppercase bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300">
                            high
                          </span>
                          <span className="text-[10px] uppercase tracking-wider text-slate-500">{a.category}</span>
                          <button
                            onClick={() => { onOpenChapter?.(a.chapter_id); onOpenChange(false); }}
                            className="text-[11px] text-blue-600 hover:underline truncate max-w-[260px]"
                            data-testid={`aggregated-action-chapter-${i}`}>
                            → {a.chapter_title}
                          </button>
                        </div>
                        <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{a.label}</p>
                        {a.suggestion && (
                          <p className="text-xs text-slate-600 dark:text-slate-400 mt-1 leading-relaxed">{a.suggestion}</p>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {/* Per-chapter table */}
            <div>
              <h4 className="text-sm font-semibold mb-2 text-slate-700 dark:text-slate-200 uppercase tracking-wider flex items-center gap-2">
                <BookOpenCheck className="h-4 w-4" /> Chapters ({chapters.length})
              </h4>
              <div className="rounded-md border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-800">
                {chapters.map((c) => {
                  const lr = c.last_readiness;
                  const s = lr?.score;
                  return (
                    <button
                      key={c.id}
                      onClick={() => { onOpenChapter?.(c.id); onOpenChange(false); }}
                      className="w-full px-3 py-2 flex items-center gap-3 hover:bg-slate-50 dark:hover:bg-slate-800/40 text-left"
                      data-testid={`project-readiness-chapter-${c.id}`}
                      style={{ paddingLeft: `${12 + (c.depth || 0) * 16}px` }}>
                      {c.depth > 0 && <span className="text-slate-400 text-xs">↳</span>}
                      <span className="flex-1 text-sm font-medium text-slate-800 dark:text-slate-200 truncate">
                        {c.title || '(untitled)'}
                      </span>
                      <span className="text-xs text-slate-400 tabular-nums">{c.words} w</span>
                      {s != null ? (
                        <>
                          <span className={`text-base font-bold tabular-nums ${scoreColor(s)}`}>{s}</span>
                          <span className="text-[10px] text-slate-400">{relTime(lr?.checked_at)}</span>
                        </>
                      ) : (
                        <span className="text-[11px] text-slate-400 italic px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800">
                          not audited
                        </span>
                      )}
                      <ChevronRight className="h-3 w-3 text-slate-400" />
                    </button>
                  );
                })}
                {chapters.length === 0 && (
                  <p className="px-3 py-6 text-center text-sm text-slate-500">No chapters yet — add chapters to begin.</p>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-between items-center gap-2 pt-3 border-t border-slate-200 dark:border-slate-800 mt-3">
          <Button variant="ghost" onClick={load} disabled={loading} data-testid="project-readiness-refresh">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={() => onOpenChange(false)} data-testid="project-readiness-close">Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BucketPill({ label, count, icon: Icon, color, testid }) {
  const palette = {
    emerald: 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-900',
    amber: 'bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-900',
    orange: 'bg-orange-50 dark:bg-orange-950/30 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-900',
    red: 'bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-900',
    slate: 'bg-slate-50 dark:bg-slate-800/60 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700',
  };
  return (
    <div className={`rounded-md border px-2.5 py-1.5 flex items-center gap-2 ${palette[color]}`} data-testid={testid}>
      <Icon className="h-4 w-4 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wider opacity-75">{label}</div>
        <div className="text-lg font-bold leading-none tabular-nums">{count}</div>
      </div>
    </div>
  );
}
